
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Info, FileText, Github, Settings, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { clearLoggingCache } from '@/utils/logger';

interface SystemInfoProps {
  showHeader?: boolean;
}

const SystemInfo: React.FC<SystemInfoProps> = ({ showHeader = true }) => {
  const [version, setVersion] = useState<string>("");
  const [releaseDate, setReleaseDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [detailedLogging, setDetailedLogging] = useState<boolean>(false);
  const [loggingLoading, setLoggingLoading] = useState<boolean>(false);

  // Загружаем информацию о версии и настройки логирования
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Загружаем версию
      try {
        const response = await fetch('/VERSION.md');
        const content = await response.text();
        
        const versionMatch = content.match(/## Текущая версия: ([\d\.]+)/);
        if (versionMatch && versionMatch[1]) {
          setVersion(versionMatch[1]);
        }
        
        const dateMatch = content.match(/\*\*Дата выпуска:\*\* ([\d\.]+)/);
        if (dateMatch && dateMatch[1]) {
          setReleaseDate(dateMatch[1]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке информации о версии:', error);
      }
      
      // Загружаем настройки логирования
      try {
        const { data } = await supabase
          .from('env_variables')
          .select('value')
          .eq('key', 'ENABLE_DETAILED_LOGGING')
          .single();
        
        setDetailedLogging(data?.value === 'true');
      } catch (error) {
        console.error('Ошибка при загрузке настроек логирования:', error);
      }
      
      setLoading(false);
    };

    loadData();
  }, []);

  const handleCheckUpdates = () => {
    toast.info('Проверка обновлений', {
      description: 'Проверка наличия новых версий на GitHub...',
      duration: 2000,
    });
    
    // Имитация проверки обновлений
    setTimeout(() => {
      toast.success('Установлена последняя версия', {
        description: `Версия ${version} является актуальной`,
      });
    }, 2000);
  };

  const handleToggleDetailedLogging = async () => {
    setLoggingLoading(true);
    
    try {
      const newValue = !detailedLogging;
      
      const { error } = await supabase
        .from('env_variables')
        .upsert({
          key: 'ENABLE_DETAILED_LOGGING',
          value: newValue.toString()
        }, {
          onConflict: 'key'
        });
      
      if (error) {
        throw error;
      }
      
      setDetailedLogging(newValue);
      clearLoggingCache(); // Сбрасываем кэш логгера
      
      toast.success(
        newValue ? 'Детальное логирование включено' : 'Детальное логирование отключено',
        {
          description: newValue 
            ? 'Теперь в консоли будут отображаться подробные логи'
            : 'Подробные логи скрыты. Ошибки и предупреждения по-прежнему отображаются'
        }
      );
    } catch (error: any) {
      console.error('Ошибка при обновлении настроек логирования:', error);
      toast.error('Ошибка при сохранении настроек', {
        description: error.message
      });
    } finally {
      setLoggingLoading(false);
    }
  };

  const handleClearConsole = () => {
    console.clear();
    toast.success('Консоль очищена');
  };

  if (loading) {
    return (
      <Card>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center">
              <Info className="h-5 w-5 mr-2" />
              О системе
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <dt className="font-medium">Версия системы:</dt>
              <Skeleton className="h-5 w-20" />
            </div>
            <div className="flex justify-between">
              <dt className="font-medium">Дата выпуска:</dt>
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {showHeader && (
        <CardHeader>
          <CardTitle className="flex items-center">
            <Info className="h-5 w-5 mr-2" />
            О системе
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <dl className="space-y-4">
          <div className="flex justify-between">
            <dt className="font-medium">Версия системы:</dt>
            <dd className="text-muted-foreground">{version || "Нет данных"}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="font-medium">Дата выпуска:</dt>
            <dd className="text-muted-foreground">{releaseDate || "Нет данных"}</dd>
          </div>
          <div className="flex items-center justify-end">
            <FileText className="h-4 w-4 mr-1 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">VERSION.md</span>
          </div>
          
          <Separator />
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Настройки логирования</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="detailed-logging" className="text-sm font-medium">
                  Детальное логирование
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Показывать подробные логи в консоли браузера
                </p>
              </div>
              <Switch
                id="detailed-logging"
                checked={detailedLogging}
                onCheckedChange={handleToggleDetailedLogging}
                disabled={loggingLoading}
              />
            </div>
            
            <Button
              onClick={handleClearConsole}
              variant="outline"
              size="sm"
              className="w-full flex items-center justify-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Очистить консоль
            </Button>
          </div>
          
          <Separator />
          
          <div className="mt-4">
            <Button 
              onClick={handleCheckUpdates} 
              variant="outline" 
              size="sm"
              className="w-full flex items-center justify-center"
            >
              <Github className="h-4 w-4 mr-2" />
              Проверить обновления
            </Button>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

export default SystemInfo;
